import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import * as socketService from '../../services/socketService';
import ConversationsList from './ConversationsList';
import ChatArea from './ChatArea';
import NewConversationModal from './NewConversationModal';
import ConversationMembersModal from './ConversationMembersModal';
import {
  getConversations,
  getMessages,
  sendMessage,
  setActiveConversation,
  updateActiveConversation,
  deleteMessage,
  getUnreadCount,
  getAvailableUsers,
  createCustomConversation,
  addConversationParticipant,
  removeConversationParticipant,
  deleteConversation,
  editMessage,
  reactToMessage,
  searchMessages,
  clearSearch,
  pinMessage,
  toggleMute
} from '../../actions/messageActions';

const MessagesContainer = ({
  auth: { user, token },
  message: { conversations, messages, activeConversation, loading, hasMore, currentPage, searchResults, isSearching },
  getConversations,
  getMessages,
  sendMessage,
  setActiveConversation,
  updateActiveConversation,
  deleteMessage,
  getUnreadCount,
  getAvailableUsers,
  createCustomConversation,
  addConversationParticipant,
  removeConversationParticipant,
  deleteConversation,
  editMessage,
  reactToMessage,
  searchMessages,
  clearSearch,
  pinMessage,
  toggleMute
}) => {
  const [showChat, setShowChat] = useState(false);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedConversationForMembers, setSelectedConversationForMembers] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const typingTimers = useRef({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  // Keep a ref to activeConversation so socket callback always sees the latest value
  const activeConversationRef = useRef(activeConversation);
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    if (!user || !token) return;

    getConversations();
    getUnreadCount();
    fetchAvailableUsers();
    fetchOnlineUsers();

    // Re-use the socket already connected by AppNavbar
    socketService.connect(token);

    const handleNewMessage = (message) => {
      getConversations(true);
      // If the new message belongs to the currently open conversation, refresh messages
      const active = activeConversationRef.current;
      if (active) {
        const isActiveConv =
          (active.type === 'direct' && message.messageType === 'direct' &&
            (message.sender?._id === active.id || message.recipient === active.id)) ||
          (active.type === 'group' && message.messageType === 'group' && message.group === active.id) ||
          (active.type === 'custom' && message.messageType === 'custom' && message.customConversation === active.id);

        if (isActiveConv) {
          getMessages(active.type, active.id, 1, true);
        }
      }
    };

    const handleUserOnline = ({ userId: uid }) => {
      setOnlineUsers(prev => new Set([...prev, uid]));
    };

    const handleUserOffline = ({ userId: uid }) => {
      setOnlineUsers(prev => { const next = new Set(prev); next.delete(uid); return next; });
    };

    const handleTypingStart = ({ conversationId, userId, userName }) => {
      setTypingUsers(prev => ({
        ...prev,
        [conversationId]: { ...prev[conversationId], [userId]: userName }
      }));
      // Auto-clear after 3 seconds if no typing_stop received
      const key = `${conversationId}_${userId}`;
      if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
      typingTimers.current[key] = setTimeout(() => {
        setTypingUsers(prev => {
          const conv = { ...prev[conversationId] };
          delete conv[userId];
          return { ...prev, [conversationId]: conv };
        });
      }, 3000);
    };

    const handleTypingStop = ({ conversationId, userId }) => {
      const key = `${conversationId}_${userId}`;
      if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
      setTypingUsers(prev => {
        const conv = { ...prev[conversationId] };
        delete conv[userId];
        return { ...prev, [conversationId]: conv };
      });
    };

    const handleMessageEdited = (editedMessage) => {
      const active = activeConversationRef.current;
      if (active) {
        getMessages(active.type, active.id, 1, true);
      }
    };

    const handleMessageReacted = ({ messageId, reactions }) => {
      // Dispatch directly to update reactions in state
      reactToMessage.__updateLocal && reactToMessage.__updateLocal(messageId, reactions);
      // Simpler: just refresh messages for active conversation
      const active = activeConversationRef.current;
      if (active) {
        getMessages(active.type, active.id, 1, true);
      }
    };

    socketService.on('new_message', handleNewMessage);
    socketService.on('message_edited', handleMessageEdited);
    socketService.on('typing_start', handleTypingStart);
    socketService.on('typing_stop', handleTypingStop);
    socketService.on('user_online', handleUserOnline);
    socketService.on('user_offline', handleUserOffline);
    const handleMessagePinned = () => {
      const active = activeConversationRef.current;
      if (active) getMessages(active.type, active.id, 1, true);
    };

    socketService.on('message_reacted', handleMessageReacted);
    socketService.on('message_pinned', handleMessagePinned);

    return () => {
      socketService.off('new_message', handleNewMessage);
      socketService.off('message_edited', handleMessageEdited);
      socketService.off('typing_start', handleTypingStart);
      socketService.off('typing_stop', handleTypingStop);
      socketService.off('user_online', handleUserOnline);
      socketService.off('user_offline', handleUserOffline);
      socketService.off('message_reacted', handleMessageReacted);
      socketService.off('message_pinned', handleMessagePinned);
    };
  }, [user, token]);

  const fetchAvailableUsers = async () => {
    try {
      const users = await getAvailableUsers();
      setAvailableUsers(users);
    } catch (error) {
      setAvailableUsers([]);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const config = { headers: { 'Content-Type': 'application/json' } };
      if (token) config.headers['Authorization'] = 'Bearer ' + token;
      const res = await axios.get('/api/messages/online-users', config);
      setOnlineUsers(new Set(res.data.onlineUserIds));
    } catch (err) {
      // silently fail
    }
  };

  const handleStartNewConversation = () => {
    setShowNewConversationModal(true);
    fetchAvailableUsers();
  };

  const handleCreateConversation = async (conversationData) => {
    try {
      // Call the createCustomConversation action
      const result = await createCustomConversation(conversationData);
      
      // Close the modal
      setShowNewConversationModal(false);
      
    } catch (error) {
      alert('Failed to create conversation. Please try again.');
    }
  };

  const handleConversationSelect = (conversation) => {
    // Validate conversation data
    if (!conversation || !conversation.id || !conversation.type) {
      return;
    }

    // If it's the same conversation, force re-render to refresh button permissions
    // Already selected — do nothing
    if (activeConversation?.id === conversation.id && activeConversation?.type === conversation.type) {
      return;
    }
    
    // Clear active conversation first to force clean state
    setActiveConversation(null);
    
    // Use requestAnimationFrame for proper React state timing
    requestAnimationFrame(() => {
      setActiveConversation(conversation);
      getMessages(conversation.type, conversation.id, 1);
      setShowChat(true);
    });
  };

  const handleBackToUsers = () => {
    setShowChat(false);
  };

  const handleSendMessage = async (messageData, attachments) => {
    try {
      await sendMessage(messageData, attachments);
      // Refresh conversations to update last message — skipLoading=true prevents tab reset
      getConversations(true);
    } catch (error) {
      // Handle specific deactivated user error
      if (error.response?.data?.code === 'RECIPIENT_DEACTIVATED' || 
          error.response?.data?.msg?.includes('deactivated')) {
        alert('Cannot send message: This user account has been deactivated.');
      } else {
        alert('Failed to send message. Please try again.');
      }
      
      throw error;
    }
  };

  const handleDeleteMessage = (messageId) => {
    deleteMessage(messageId);
  };

  const typingTimeoutRef = useRef(null);
  const handleTyping = useCallback(() => {
    if (!activeConversation || !user) return;
    const userName = `${user.name || ''} ${user.lastName || ''}`.trim();
    socketService.emit('typing_start', {
      conversationId: activeConversation.id,
      conversationType: activeConversation.type,
      userName
    });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socketService.emit('typing_stop', {
        conversationId: activeConversation.id,
        conversationType: activeConversation.type
      });
    }, 2000);
  }, [activeConversation, user]);

  const handleSearch = (query) => {
    if (!query.trim()) {
      clearSearch();
      return;
    }
    if (activeConversation) {
      searchMessages(query, activeConversation.id, activeConversation.type);
    } else {
      searchMessages(query);
    }
  };

  const handleClearSearch = () => {
    clearSearch();
  };

  const handleToggleMute = (type, id) => {
    toggleMute(type, id);
  };

  const handlePinMessage = (messageId) => {
    pinMessage(messageId);
  };

  const handleReactToMessage = (messageId, emoji) => {
    reactToMessage(messageId, emoji);
  };

  const handleEditMessage = async (messageId, content) => {
    try {
      await editMessage(messageId, content);
    } catch (error) {
      alert('Failed to edit message. Please try again.');
    }
  };

  const handleLoadMore = () => {
    if (activeConversation && hasMore && !loading) {
      getMessages(activeConversation.type, activeConversation.id, currentPage + 1);
    }
  };

  const handleManageMembers = (conversation) => {
    setSelectedConversationForMembers(conversation);
    setShowMembersModal(true);
  };

  const handleAddParticipant = async (conversationId, userId) => {
    try {
      const result = await addConversationParticipant(conversationId, userId);
      
      // Update the selected conversation for the modal with the new participant
      if (selectedConversationForMembers && selectedConversationForMembers.id === conversationId && result.conversation) {
        const updatedConversation = {
          ...selectedConversationForMembers,
          participants: result.conversation.participants
        };
        setSelectedConversationForMembers(updatedConversation);
        
        // Also update activeConversation if it's the same conversation being modified
        if (activeConversation && activeConversation.id === conversationId) {
          updateActiveConversation(updatedConversation);
        }
      }
      
    } catch (error) {
      throw error;
    }
  };

  const handleRemoveParticipant = async (conversationId, userId) => {
    try {
      await removeConversationParticipant(conversationId, userId);
      
      // Update the selected conversation for the modal
      if (selectedConversationForMembers && selectedConversationForMembers.id === conversationId) {
        const updatedParticipants = selectedConversationForMembers.participants.filter(p => p._id !== userId);
        const updatedConversation = {
          ...selectedConversationForMembers,
          participants: updatedParticipants
        };
        setSelectedConversationForMembers(updatedConversation);
        
        // Also update activeConversation if it's the same conversation being modified
        if (activeConversation && activeConversation.id === conversationId) {
          updateActiveConversation(updatedConversation);
        }
      }
      
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      await deleteConversation(conversationId);
      
      // Close modal after successful deletion
      setShowMembersModal(false);
      setSelectedConversationForMembers(null);
      
      // If the deleted conversation was active, clear it
      if (activeConversation && activeConversation.id === conversationId) {
        setActiveConversation(null);
        setShowChat(false);
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <div>
      {/* Main Chat Interface */}
      <div className="rounded-xl shadow-sm border border-border overflow-hidden bg-background">
        <div className="flex h-[calc(100vh-8rem)] lg:h-[calc(100vh-5rem)]">
          <ConversationsList
            conversations={conversations}
            selectedConversation={activeConversation}
            onConversationSelect={handleConversationSelect}
            onStartNewConversation={handleStartNewConversation}
            showChat={showChat}
            loading={loading}
            availableUsersCount={availableUsers.length}
            onlineUsers={onlineUsers}
            onToggleMute={handleToggleMute}
          />
          
          <ChatArea
            key={`chat-${activeConversation?.id}-${activeConversation?.creator?._id}-${user?.id}-${user?.role}`}
            conversation={activeConversation}
            messages={messages}
            currentUser={user}
            onSendMessage={handleSendMessage}
            onBackToUsers={handleBackToUsers}
            onDeleteMessage={handleDeleteMessage}
            onEditMessage={handleEditMessage}
            onReactToMessage={handleReactToMessage}
            onPinMessage={handlePinMessage}
            onSearch={handleSearch}
            onClearSearch={handleClearSearch}
            searchResults={searchResults}
            isSearching={isSearching}
            onTyping={handleTyping}
            typingUsers={activeConversation ? typingUsers[activeConversation.id] : null}
            showChat={showChat}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onManageMembers={handleManageMembers}
          />
        </div>
      </div>

      {/* New Conversation Modal */}
      <NewConversationModal
        isOpen={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onCreateConversation={handleCreateConversation}
        currentUser={user}
        availableUsers={availableUsers}
      />

      {/* Conversation Members Modal */}
      <ConversationMembersModal
        isOpen={showMembersModal}
        onClose={() => {
          setShowMembersModal(false);
          setSelectedConversationForMembers(null);
        }}
        conversation={selectedConversationForMembers}
        currentUser={user}
        availableUsers={availableUsers}
        onAddParticipant={handleAddParticipant}
        onRemoveParticipant={handleRemoveParticipant}
        onDeleteConversation={handleDeleteConversation}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  message: state.message
});

export default connect(mapStateToProps, {
  getConversations,
  getMessages,
  sendMessage,
  setActiveConversation,
  updateActiveConversation,
  deleteMessage,
  getUnreadCount,
  getAvailableUsers,
  createCustomConversation,
  addConversationParticipant,
  removeConversationParticipant,
  deleteConversation,
  editMessage,
  reactToMessage,
  searchMessages,
  clearSearch,
  pinMessage,
  toggleMute
})(MessagesContainer);
